import * as React from "react";
import MonacoEditor from "@monaco-editor/react";
import Markdown, { Components, ExtraProps } from "react-markdown";
import { useMemo } from "react";
import remarkGfm from "remark-gfm";
import { BsCopy } from "react-icons/bs";
import { Button } from "react-bootstrap";
import { notificationStore } from "lib/store";
import I18n from "lib/I18n";

const widthMargin = 20;

interface CustomEditorProps {
  value: string;
  language: string;
  width: number;
}

export function CustomEditor(props: CustomEditorProps) {
  const { value, language, width } = props;

  const onCopy = () => {
    navigator.clipboard.writeText(value);
    notificationStore.notifySuccess(
      I18n.t("bigquery_query_viewer.messages.copied_to_clipboard"),
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <MonacoEditor
        value={value}
        width={width - widthMargin}
        language={language}
        options={{
          scrollBeyondLastLine: false,
          wordWrap: "on",
          wrappingStrategy: "advanced",
          readOnly: true,
          minimap: {
            enabled: false,
          },
          overviewRulerLanes: 0,
          scrollbar: {
            handleMouseWheel: false,
          },
        }}
        onMount={(editor) => {
          const updateHeight = () => {
            const container = editor.getDomNode();
            if (!container) {
              return;
            }
            const contentHeight = editor.getContentHeight();
            container.style.width = `${width - widthMargin}px`;
            container.style.height = `${contentHeight}px`;
            editor.layout({
              width: width - widthMargin,
              height: contentHeight,
            });
          };
          editor.onDidChangeModelContent(updateHeight);
          editor.onDidContentSizeChange(updateHeight);
          updateHeight();
        }}
      />
      <div style={{ position: "absolute", top: 2, right: 2 }}>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={onCopy}
          style={{ padding: "2px 4px" }}
        >
          <BsCopy size={16} />
        </Button>
      </div>
    </div>
  );
}

export function CustomTable(
  props: React.ClassAttributes<HTMLElement> &
    React.HTMLAttributes<HTMLElement> &
    ExtraProps,
) {
  return <table className="table table-striped">{props.children}</table>;
}

interface CustomMarkdownProps {
  value: string;
  width: number;
}

export function CustomMarkdown(props: CustomMarkdownProps) {
  const { value, width } = props;

  const customCode = useMemo<Components["code"]>(
    () => (props) => {
      const { children, className, node, ...rest } = props;
      const match = /language-(\w+)/.exec(className || "");
      return match && typeof children == "string" ? (
        <CustomEditor
          {...rest}
          value={children.replace(/\n$/, "")}
          language={match[1]}
          width={width}
        />
      ) : (
        <code {...rest} className={className}>
          {children}
        </code>
      );
    },
    [width],
  );

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        code: customCode,
        table: CustomTable,
      }}
    >
      {value}
    </Markdown>
  );
}
